import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HreflangMeta from '../components/HreflangMeta';
import articles from '../data/blog-data.json';
import './Blog.css';

const BASE_URL = 'https://libbenagents.com';

function formatDate(dateStr, locale) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(locale === 'en' ? 'en-US' : 'pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function buildBlogJsonLd(localizedArticles) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Libben Agents Blog',
    url: `${BASE_URL}/blog`,
    blogPost: localizedArticles.map((article) => ({
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.description,
      datePublished: article.publishedAt,
      url: `${BASE_URL}/blog/${article.slug}`,
      image: article.ogImage ? `${BASE_URL}${article.ogImage}` : undefined,
    })),
  };
}

export default function Blog() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en' : 'pt';

  const localizedArticles = articles.map((article) => ({
    slug: article.slug,
    publishedAt: article.publishedAt,
    ogImage: article.ogImage,
    title: article[locale].title,
    description: article[locale].description,
  }));

  const jsonLd = buildBlogJsonLd(localizedArticles);

  return (
    <div className="blog-page">
      <Helmet>
        <title>{t('blog.metaTitle')}</title>
        <meta name="description" content={t('blog.metaDescription')} />
        <meta property="og:title" content={t('blog.metaTitle')} />
        <meta property="og:description" content={t('blog.metaDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BASE_URL}/blog`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <HreflangMeta />

      <section className="blog-hero">
        <h1 className="blog-hero-title">{t('blog.title')}</h1>
        <p className="blog-hero-subtitle">{t('blog.subtitle')}</p>
      </section>

      <section className="blog-listing">
        {localizedArticles.map((article) => (
          <Link
            key={article.slug}
            to={`/blog/${article.slug}`}
            className="blog-card"
          >
            {article.ogImage && (
              <div className="blog-card-image">
                <img src={article.ogImage} alt={article.title} loading="lazy" />
              </div>
            )}
            <div className="blog-card-content">
              <span className="blog-card-date">
                {formatDate(article.publishedAt, locale)}
              </span>
              <h2 className="blog-card-title">{article.title}</h2>
              <p className="blog-card-description">{article.description}</p>
              <span className="blog-card-link">{t('blog.readMore')}</span>
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}
